/**
 * Punkte-Navigation und Pfeile — für vertikale Wrapper (fixed)
 * und horizontale Slider (absolut im Slider positioniert).
 */

import { formatLabel } from './utils.js';

const ARROW_SVG =
	'<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

export class Navigation {
	/**
	 * @param {Element} host    Element, an das die Navigation angehängt wird.
	 * @param {Object}  options
	 *   axis      'v' | 'h'
	 *   count     Anzahl der Ziele
	 *   dots      bool
	 *   arrows    bool
	 *   loop      bool
	 *   navLabel  aria-label der <nav>
	 *   itemLabel i18n-Vorlage („Sektion %d")
	 *   prevLabel / nextLabel
	 *   labels    optionale Klartext-Labels je Ziel (z. B. Anchor-Namen)
	 *   onSelect(index)
	 *   onStep(direction)
	 */
	constructor( host, options ) {
		this.host = host;
		this.options = options;
		this.dots = [];
		this.arrowPrev = null;
		this.arrowNext = null;
		this.nav = null;
		this.index = 0;

		if ( options.dots ) {
			this.buildDots();
		}

		if ( options.arrows ) {
			this.buildArrows();
		}
	}

	buildDots() {
		const { axis, count, navLabel, itemLabel, labels, onSelect } = this.options;

		this.nav = document.createElement( 'nav' );
		this.nav.className = `sfps-nav sfps-nav--${ axis }`;
		this.nav.setAttribute( 'aria-label', navLabel );

		for ( let i = 0; i < count; i++ ) {
			const dot = document.createElement( 'button' );
			dot.type = 'button';
			dot.className = 'sfps-nav__dot';
			dot.setAttribute(
				'aria-label',
				( labels && labels[ i ] ) || formatLabel( itemLabel, i + 1 )
			);
			dot.addEventListener( 'click', () => onSelect( i ) );
			this.nav.appendChild( dot );
			this.dots.push( dot );
		}

		this.host.appendChild( this.nav );
	}

	buildArrows() {
		const { axis, prevLabel, nextLabel, onStep } = this.options;

		const make = ( direction, label ) => {
			const button = document.createElement( 'button' );
			button.type = 'button';
			button.className = `sfps-arrow sfps-arrow--${ axis } sfps-arrow--${
				direction > 0 ? 'next' : 'prev'
			}`;
			button.setAttribute( 'aria-label', label );
			button.innerHTML = ARROW_SVG;
			button.addEventListener( 'click', () => onStep( direction ) );
			this.host.appendChild( button );
			return button;
		};

		this.arrowPrev = make( -1, prevLabel );
		this.arrowNext = make( 1, nextLabel );
	}

	setActive( index ) {
		this.index = index;

		this.dots.forEach( ( dot, i ) => {
			dot.classList.toggle( 'is-active', i === index );

			if ( i === index ) {
				dot.setAttribute( 'aria-current', 'true' );
			} else {
				dot.removeAttribute( 'aria-current' );
			}
		} );

		if ( ! this.options.loop ) {
			if ( this.arrowPrev ) {
				this.arrowPrev.disabled = index === 0;
			}

			if ( this.arrowNext ) {
				this.arrowNext.disabled = index === this.options.count - 1;
			}
		}
	}

	destroy() {
		if ( this.nav ) {
			this.nav.remove();
		}

		if ( this.arrowPrev ) {
			this.arrowPrev.remove();
		}

		if ( this.arrowNext ) {
			this.arrowNext.remove();
		}

		this.dots = [];
	}
}
